import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../hooks/useAuth";
import { useCart } from "../hooks/useCart";
import { useOrderOnBehalf } from "../hooks/useOrderOnBehalf";
import { useSSRData } from "../contexts/SSRDataContext";
import SEO from "../components/SEO";
import { getPlaceholderWebP, getProductPlaceholder } from "../lib/productPlaceholder";
import { slugify } from "@shared/slug";
import { extractSearchTerm } from "../lib/gs1";
import { useQuery } from "@tanstack/react-query";
import { ProductListCard } from "../components/ProductListCard";
import { trackEvent } from "../lib/analytics";
import { caseCountToUnits, getCaseQuantity } from "../../../shared/caseQuantity";

const BarcodeScanner = lazy(() => import("../components/BarcodeScanner"));

function CatalogGridImage({
  imageUrl,
  productId,
  dosageForm,
  route,
  alt,
}: {
  imageUrl?: string | null;
  productId: number;
  dosageForm?: string | null;
  route?: string | null;
  alt: string;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const placeholder = getProductPlaceholder(productId, dosageForm, route);

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl, productId]);

  if (imageUrl && !imageFailed) {
    return (
      <img
        src={imageUrl}
        alt={alt}
        className="w-full h-full object-contain"
        loading="lazy"
        onError={() => setImageFailed(true)}
      />
    );
  }

  return (
    <picture className="block w-full h-full">
      <source srcSet={getPlaceholderWebP(placeholder)} type="image/webp" />
      <img
        src={placeholder}
        alt={alt}
        className="block w-full h-full object-contain"
        loading="lazy"
      />
    </picture>
  );
}

function CatalogSkeleton({ count = 12, mode = "list" }: { count?: number; mode?: "list" | "grid" }) {
  if (mode === "grid") {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-lg p-3 animate-pulse">
            <div className="w-full aspect-square bg-gray-200 rounded-md mb-3" />
            <div className="h-3 bg-gray-200 rounded mb-1.5 w-full" />
            <div className="h-2.5 bg-gray-100 rounded mb-1 w-2/3" />
            <div className="h-2.5 bg-gray-100 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="divide-y divide-gray-100">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
          <div className="w-5 h-5 bg-gray-200 rounded flex-shrink-0" />
          <div className="w-10 h-10 bg-gray-200 rounded-md flex-shrink-0" />
          <div className="flex-1 min-w-0 grid grid-cols-[1fr_120px_100px_1fr_80px] gap-3 items-center">
            <div className="h-3.5 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-100 rounded w-full" />
            <div className="h-3 bg-gray-100 rounded w-full" />
            <div className="h-3 bg-gray-100 rounded w-2/3" />
            <div className="h-3 bg-gray-200 rounded w-full ml-auto" />
          </div>
        </div>
      ))}
    </div>
  );
}

function buildPageHref(params: URLSearchParams, targetPage: number): string {
  const next = new URLSearchParams(params);
  if (targetPage <= 1) next.delete("page");
  else next.set("page", String(targetPage));
  const qs = next.toString();
  return qs ? `/products?${qs}` : "/products";
}

interface FilterOptions {
  manufacturers: string[];
  dosageForms: string[];
  routes: string[];
}

const FEATURED_CATEGORY_LINKS = [
  { name: "Pharmaceuticals", slug: "pharmaceuticals" },
  { name: "Animal Health", slug: "animal-health" },
  { name: "Medical/Surgical", slug: "medical-surgical" },
  { name: "Caps & Tabs", slug: "caps-and-tabs" },
  { name: "Diabetic Supplies", slug: "diabetic-supplies" },
];

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { customer: onBehalfCustomer } = useOrderOnBehalf();
  const { catalogPage: ssrCatalogPage } = useSSRData();
  const [addingToCart, setAddingToCart] = useState<Record<number, boolean>>({});
  const [addedToCart, setAddedToCart] = useState<Record<number, boolean>>({});
  const [categories, setCategories] = useState<any[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ manufacturers: [], dosageForms: [], routes: [] });
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setSearchParamsRef = useRef(setSearchParams);
  const isMountedRef = useRef(false);
  useEffect(() => { setSearchParamsRef.current = setSearchParams; });

  // All filter/sort/search/page state lives in the URL so every catalog state
  // has a stable, shareable address. robots.txt keeps these duplicate facets
  // out of crawler queues while the canonical consolidates them to /products.
  const category = searchParams.get("category") || "";
  const manufacturer = searchParams.get("manufacturer") || "";
  const dosageForm = searchParams.get("dosageForm") || "";
  const route = searchParams.get("route") || "";
  const inStockOnly = searchParams.get("inStock") === "true";
  const strength = searchParams.get("strength") || "";
  const sort = searchParams.get("sort") || "enrichment_desc";
  const urlSearch = searchParams.get("search") || "";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);

  // Local state for the search input so typing stays smooth; synced to URL on debounce.
  const [search, setSearch] = useState(urlSearch);
  const [scannerOpen, setScannerOpen] = useState(false);

  const handleScan = useCallback((value: string) => {
    setScannerOpen(false);
    setSearch(value);
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      if (value.trim()) n.set("search", value.trim());
      else n.delete("search");
      n.delete("page");
      return n;
    }, { replace: true });
  }, [setSearchParams]);

  // Helper: update one or more URL params and reset page to 1.
  const setFilter = useCallback((updates: Record<string, string | boolean | null>) => {
    const [filterName] = Object.keys(updates);
    if (filterName) {
      trackEvent("catalog_filter_changed", { filter: filterName });
    }
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      for (const [key, val] of Object.entries(updates)) {
        if (val === null || val === false || val === "") n.delete(key);
        else n.set(key, String(val));
      }
      n.delete("page");
      return n;
    }, { replace: true });
  }, [setSearchParams]);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "grid">(() => {
    if (typeof window === "undefined") return "list";
    const saved = window.localStorage.getItem("products:viewMode");
    return saved === "grid" || saved === "list" ? saved : "list";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("products:viewMode", viewMode);
  }, [viewMode]);
  const [topManufacturers, setTopManufacturers] = useState<{ name: string; slug: string; productCount: number }[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(new Set());
  const [customerPriceMap, setCustomerPriceMap] = useState<Record<number, string>>({});
  const [lastOrdered, setLastOrdered] = useState<any[]>([]);
  const [requestingProduct, setRequestingProduct] = useState<Record<number, boolean>>({});
  const [requestedProducts, setRequestedProducts] = useState<Set<number>>(new Set());
  const [quantities, setQuantities] = useState<Record<number, number>>({});

  const [rawInputs, setRawInputs] = useState<Record<number, string>>({});

  const getQty = (id: number) => quantities[id] ?? 1;
  const getRaw = (id: number) => id in rawInputs ? rawInputs[id] : String(getQty(id));

  const stepQty = useCallback((id: number, delta: number) => {
    setQuantities(prev => {
      const current = prev[id] ?? 1;
      const safe = Math.max(1, Math.floor(current + delta) || 1);
      return { ...prev, [id]: safe };
    });
    setRawInputs(prev => { const next = { ...prev }; delete next[id]; return next; });
  }, []);

  const commitRaw = useCallback((id: number) => {
    setRawInputs(prev => {
      const parsed = parseInt(prev[id] ?? "");
      const safe = isNaN(parsed) ? 1 : Math.max(1, parsed);
      setQuantities(q => ({ ...q, [id]: safe }));
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const handleSetRaw = useCallback((id: number, val: string) => {
    setRawInputs(prev => ({ ...prev, [id]: val }));
  }, []);

  const limit = 24;

  const catalogQueryKey = useMemo(
    () => ["catalog", urlSearch, category, manufacturer, dosageForm, route, inStockOnly ? "true" : "false", strength, sort, page],
    [urlSearch, category, manufacturer, dosageForm, route, inStockOnly, strength, sort, page]
  );
  const { data: catalogData, isLoading: loading, isFetching, error: catalogQueryError, refetch } = useQuery({
    queryKey: catalogQueryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (urlSearch) params.set("search", urlSearch);
      if (category) params.set("category", category);
      if (manufacturer) params.set("manufacturer", manufacturer);
      if (dosageForm) params.set("dosageForm", dosageForm);
      if (route) params.set("route", route);
      if (inStockOnly) params.set("inStock", "true");
      if (strength) params.set("strength", strength);
      if (sort) params.set("sort", sort);
      params.set("page", String(page));
      params.set("limit", String(limit));
      const r = await fetch(`/api/products?${params}`);
      if (!r.ok) throw new Error("Failed to load products");
      return r.json() as Promise<{ products: any[]; total: number }>;
    },
    staleTime: 60 * 1000,
    retry: 2,
    placeholderData: (previousData) => {
      if (previousData) return previousData;
      if (ssrCatalogPage && page === 1 && !urlSearch && !category && !manufacturer) {
        return { products: ssrCatalogPage.products, total: ssrCatalogPage.total };
      }
      return undefined;
    },
  });
  const products = catalogData?.products ?? [];
  const total = catalogData?.total ?? 0;
  const error = catalogQueryError ? (catalogQueryError instanceof Error ? catalogQueryError.message : "Failed to load products") : "";

  useEffect(() => {
    fetch("/api/products/categories")
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setCategories(data); })
      .catch(() => {});
    fetch("/api/seo/manufacturers")
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (Array.isArray(data)) setTopManufacturers(data.slice(0, 16)); })
      .catch(() => {});
    fetch("/api/products/filters")
      .then(r => r.json())
      .then(data => {
        if (data && Array.isArray(data.manufacturers)) setFilterOptions(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      const idsUrl = onBehalfCustomer
        ? `/api/favorites/ids?customerId=${onBehalfCustomer.id}`
        : "/api/favorites/ids";
      fetch(idsUrl, { credentials: "include" })
        .then(r => r.json())
        .then((ids: number[]) => setFavoriteIds(new Set(ids)))
        .catch(() => {});
    }
  }, [user, onBehalfCustomer]);

  useEffect(() => {
    if (user && (onBehalfCustomer || user.accountStatus === "active")) {
      const priceUrl = onBehalfCustomer
        ? `/api/customer-prices/for-customer/${onBehalfCustomer.id}/all-prices`
        : "/api/customer-prices/my-prices";
      fetch(priceUrl, { credentials: "include", cache: "no-store" })
        .then(r => r.ok ? r.json() : {})
        .then(data => setCustomerPriceMap(data))
        .catch(() => {});
    }
  }, [user, onBehalfCustomer]);

  useEffect(() => {
    if (user && user.accountStatus === "active") {
      fetch("/api/products/last-ordered", { credentials: "include" })
        .then(r => r.ok ? r.json() : [])
        .then(data => { if (Array.isArray(data)) setLastOrdered(data); })
        .catch(() => {});
    }
  }, [user]);


  const toggleFavorite = useCallback(async (productId: number) => {
    if (!user) return;
    try {
      const toggleUrl = onBehalfCustomer
        ? `/api/favorites/${productId}?customerId=${onBehalfCustomer.id}`
        : `/api/favorites/${productId}`;
      const res = await fetch(toggleUrl, { method: "POST", credentials: "include" });
      if (!res.ok) {
        toast.error("Something went wrong. Please try again.");
        return;
      }
      const data = await res.json();
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (data.favorited) next.add(productId);
        else next.delete(productId);
        return next;
      });
      trackEvent("favorite_toggled", {
        product_id: productId,
        favorited: Boolean(data.favorited),
        source: "catalog",
      });
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  }, [user, onBehalfCustomer]);

  const handleAddToCart = useCallback(async (e: React.MouseEvent, productId: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (addingToCart[productId] || addedToCart[productId]) return;
    setAddingToCart(prev => ({ ...prev, [productId]: true }));
    const product = products.find(p => p.id === productId);
    const caseCount = getQty(productId);
    const unitQuantity = caseCountToUnits(caseCount, product?.ndc);
    const result = await addToCart(productId, unitQuantity);
    setAddingToCart(prev => ({ ...prev, [productId]: false }));
    if (result.success) {
      trackEvent("add_to_cart", {
        product_id: productId,
        quantity: unitQuantity,
        ...(getCaseQuantity(product?.ndc) ? { case_count: caseCount } : {}),
        source: "catalog",
      });
      setAddedToCart(prev => ({ ...prev, [productId]: true }));
      setTimeout(() => setAddedToCart(prev => ({ ...prev, [productId]: false })), 2000);
      if (result.warning) toast.warning(result.warning);
    } else if (result.error) {
      toast.error(result.error);
    }
  }, [addToCart, addingToCart, addedToCart, quantities, products]);

  const handleRequestSpecialOrder = useCallback(async (e: React.MouseEvent, productId: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (requestingProduct[productId] || requestedProducts.has(productId)) return;
    setRequestingProduct(prev => ({ ...prev, [productId]: true }));
    try {
      const res = await fetch("/api/products/2day-shipping-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ productId }),
      });
      if (res.ok) {
        trackEvent("product_request_submitted", {
          product_id: productId,
          source: "catalog",
        });
        setRequestedProducts(prev => new Set(prev).add(productId));
        const data = await res.json().catch(() => ({}));
        toast.success(
          data.message ||
            "Request received! We'll be in touch shortly with next steps for 2-day shipping."
        );
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || "Failed to submit request. Please try again.");
      }
    } catch {
      toast.error("Failed to submit request. Please try again.");
    }
    setRequestingProduct(prev => ({ ...prev, [productId]: false }));
  }, [requestingProduct, requestedProducts]);

  const canAddToCart = user && user.accountStatus === "active";

  const activeFilterCount = [category, manufacturer, dosageForm, route, strength].filter(Boolean).length + (inStockOnly ? 1 : 0);

  const clearFilters = useCallback(() => {
    setSearch("");
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Debounce search input → sync to URL (also resets page).
  // Uses a ref for setSearchParams so this effect only re-fires when the
  // search string actually changes — not when setSearchParams changes identity
  // (which happens on every render in React Router v7 and would reset the page
  // back to 1 after every pagination click).
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchParamsRef.current(prev => {
        const n = new URLSearchParams(prev);
        const trimmed = search.trim();
        if (trimmed) n.set("search", trimmed);
        else n.delete("search");
        n.delete("page");
        return n;
      }, { replace: true });
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [search]);


  const totalPages = Math.ceil(total / limit);

  const noSearchActive = !urlSearch;
  const showFavoriteSections = noSearchActive && user && favoriteIds.size > 0;
  const favoritesInView = showFavoriteSections
    ? products.filter(p => favoriteIds.has(p.id)).length
    : 0;

  return (
    <>
      <SEO
        title="Wholesale Pharmaceutical Catalog"
        description="Browse Premier Pharma's wholesale catalog: generics, animal health, medical/surgical supplies, and diabetic supplies. NDC search and live inventory."
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Products", path: "/products" },
        ]}
      />
      <section className="py-6 bg-background min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-5">
            <h1 className="text-2xl font-bold text-primary">Wholesale Pharmaceutical Catalog</h1>
            <span className="text-sm text-gray-500">{total} product{total !== 1 ? "s" : ""}</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1 flex items-center">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <input
                type="text"
                aria-label="Search products by name, NDC, or manufacturer"
                placeholder="Search by name, NDC, or manufacturer... (or scan with a barcode scanner)"
                className="input-field w-full pl-9 pr-10"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => {
                  // Hardware/HID barcode scanners act like a keyboard: they type the decoded
                  // barcode value into whatever input has focus, then send an Enter keystroke.
                  // Run the raw scanned value through the same GS1/NDC parsing used by the
                  // camera scanner before searching, so it isn't searched for verbatim.
                  if (e.key !== "Enter") return;
                  const raw = e.currentTarget.value;
                  const parsed = extractSearchTerm(raw);
                  if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                  setSearch(parsed);
                  trackEvent("catalog_search", {
                    has_query: Boolean(parsed.trim()),
                    input_method: "keyboard",
                  });
                  setSearchParams(prev => {
                    const n = new URLSearchParams(prev);
                    const trimmed = parsed.trim();
                    if (trimmed) n.set("search", trimmed);
                    else n.delete("search");
                    n.delete("page");
                    return n;
                  }, { replace: true });
                }}
              />
              <button
                type="button"
                onClick={() => setScannerOpen(true)}
                title="Scan barcode"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-400 hover:text-primary hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1"
                aria-label="Scan barcode"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2" />
                  <line x1="7" y1="8" x2="7" y2="16" strokeWidth={2} />
                  <line x1="10" y1="8" x2="10" y2="16" strokeWidth={1} />
                  <line x1="12.5" y1="8" x2="12.5" y2="16" strokeWidth={2} />
                  <line x1="15" y1="8" x2="15" y2="16" strokeWidth={1} />
                  <line x1="17" y1="8" x2="17" y2="16" strokeWidth={2} />
                </svg>
              </button>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setFiltersOpen(!filtersOpen)}
                aria-expanded={filtersOpen}
                aria-controls="catalog-filter-panel"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors flex-1 sm:flex-none justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 ${filtersOpen || activeFilterCount > 0 ? "bg-primary text-white border-primary" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
                Filters
                {activeFilterCount > 0 && (
                  <span className="bg-white text-primary text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{activeFilterCount}</span>
                )}
              </button>
              <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-0.5 bg-white">
                <button
                  onClick={() => setViewMode("list")}
                  aria-label="List view"
                  aria-pressed={viewMode === "list"}
                  className={`p-1.5 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 ${viewMode === "list" ? "bg-primary text-white" : "text-gray-400 hover:text-gray-600"}`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                </button>
                <button
                  onClick={() => setViewMode("grid")}
                  aria-label="Grid view"
                  aria-pressed={viewMode === "grid"}
                  className={`p-1.5 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 ${viewMode === "grid" ? "bg-primary text-white" : "text-gray-400 hover:text-gray-600"}`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                </button>
              </div>
              <select
                aria-label="Sort products by"
                className="input-field flex-1 sm:flex-none sm:w-[180px]"
                value={sort}
                onChange={e => setFilter({ sort: e.target.value })}
              >
                <optgroup label="Name">
                  <option value="name_asc">Name A-Z</option>
                  <option value="name_desc">Name Z-A</option>
                </optgroup>
                <optgroup label="Price">
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                </optgroup>
                <optgroup label="Manufacturer">
                  <option value="manufacturer_asc">Manufacturer A-Z</option>
                  <option value="manufacturer_desc">Manufacturer Z-A</option>
                </optgroup>
                <optgroup label="NDC">
                  <option value="ndc_asc">NDC Ascending</option>
                  <option value="ndc_desc">NDC Descending</option>
                </optgroup>
                <optgroup label="Date Added">
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </optgroup>
                <optgroup label="Data Quality">
                  <option value="enrichment_desc">Best Data First</option>
                </optgroup>
              </select>
            </div>
          </div>

          {filtersOpen && (
            <div id="catalog-filter-panel" className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div>
                  <label htmlFor="filter-category" className="block text-xs font-medium text-gray-500 mb-1">Category</label>
                  <select id="filter-category" className="input-field text-sm w-full" value={category} onChange={e => setFilter({ category: e.target.value })}>
                    <option value="">All</option>
                    {categories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="filter-manufacturer" className="block text-xs font-medium text-gray-500 mb-1">Manufacturer</label>
                  <select id="filter-manufacturer" className="input-field text-sm w-full" value={manufacturer} onChange={e => setFilter({ manufacturer: e.target.value })}>
                    <option value="">All</option>
                    {filterOptions.manufacturers.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="filter-dosage-form" className="block text-xs font-medium text-gray-500 mb-1">Dosage Form</label>
                  <select id="filter-dosage-form" className="input-field text-sm w-full" value={dosageForm} onChange={e => setFilter({ dosageForm: e.target.value })}>
                    <option value="">All</option>
                    {filterOptions.dosageForms.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="filter-route" className="block text-xs font-medium text-gray-500 mb-1">Route</label>
                  <select id="filter-route" className="input-field text-sm w-full" value={route} onChange={e => setFilter({ route: e.target.value })}>
                    <option value="">All</option>
                    {filterOptions.routes.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="filter-strength" className="block text-xs font-medium text-gray-500 mb-1">Strength</label>
                  <input id="filter-strength" className="input-field text-sm w-full" placeholder="e.g. 500MG" value={strength} onChange={e => setFilter({ strength: e.target.value })} />
                </div>
                <div className="flex flex-col justify-between">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mt-5">
                    <input type="checkbox" checked={inStockOnly} onChange={e => setFilter({ inStock: e.target.checked ? "true" : null })} className="rounded border-gray-300 text-primary focus:ring-primary" />
                    In Stock Only
                  </label>
                </div>
              </div>
              {activeFilterCount > 0 && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-500">{activeFilterCount} filter{activeFilterCount !== 1 ? "s" : ""} active</span>
                  <button onClick={clearFilters} className="text-xs text-primary hover:text-primary/80 font-medium">Clear all</button>
                  <div className="flex flex-wrap gap-1 ml-2">
                    {category && <FilterTag label={categories.find((c: any) => String(c.id) === category)?.name || "Category"} onRemove={() => setFilter({ category: null })} />}
                    {manufacturer && <FilterTag label={manufacturer} onRemove={() => setFilter({ manufacturer: null })} />}
                    {dosageForm && <FilterTag label={dosageForm} onRemove={() => setFilter({ dosageForm: null })} />}
                    {route && <FilterTag label={route} onRemove={() => setFilter({ route: null })} />}
                    {strength && <FilterTag label={`Strength: ${strength}`} onRemove={() => setFilter({ strength: null })} />}
                    {inStockOnly && <FilterTag label="In Stock" onRemove={() => setFilter({ inStock: null })} />}
                  </div>
                </div>
              )}
            </div>
          )}

          {canAddToCart && lastOrdered.length > 0 && !urlSearch && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <h2 className="text-sm font-semibold text-primary uppercase tracking-wider">Your Last Ordered Items</h2>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin">
                {lastOrdered.map((item: any) => (
                  <Link
                    key={item.id}
                    to={`/products/${item.id}`}
                    className="flex-shrink-0 w-[160px] bg-white border border-gray-200 rounded-lg p-3 hover:border-accent/40 hover:shadow-sm transition-all group flex flex-col"
                  >
                    <div className="w-full h-20 bg-gray-50 rounded flex items-center justify-center mb-2 overflow-hidden">
                      <CatalogGridImage
                        imageUrl={item.imageUrl}
                        productId={item.id}
                        dosageForm={item.dosageForm}
                        route={item.route}
                        alt={[item.name, item.strength, item.dosageForm].filter(Boolean).join(" ")}
                      />
                    </div>
                    <h3 className="text-xs font-medium text-primary group-hover:text-accent truncate leading-tight">{item.name}</h3>
                    <p className="text-[10px] text-gray-600 truncate mt-0.5">{(item.manufacturer && item.manufacturer !== "Unknown" ? item.manufacturer : null) || item.labelerName || "—"}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${item.inStock ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-600"}`}>
                        <span className={`w-1 h-1 rounded-full ${item.inStock ? "bg-green-500" : "bg-blue-400"}`}></span>
                        {item.inStock ? "In Stock" : "Request Item"}
                      </span>
                      {customerPriceMap[item.id] ? (
                        <span className="text-xs font-semibold text-green-600">${customerPriceMap[item.id]}</span>
                      ) : item.price && parseFloat(item.price) > 0 ? (
                        <span className="text-xs font-semibold text-accent">${item.price}</span>
                      ) : null}
                    </div>
                    {item.inStock && (
                      <div className="mt-auto pt-2" onClick={e => e.preventDefault()}>
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <button aria-label={`Decrease quantity for ${item.name}`} onClick={e => { e.preventDefault(); e.stopPropagation(); stepQty(item.id, -1); }} className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold leading-none">−</button>
                          <input
                            type="number"
                            aria-label={`Quantity for ${item.name}`}
                            min={1}
                            value={getRaw(item.id)}
                            onChange={e => { e.stopPropagation(); setRawInputs(prev => ({ ...prev, [item.id]: e.target.value })); }}
                            onBlur={() => commitRaw(item.id)}
                            onFocus={e => e.target.select()}
                            onClick={e => e.preventDefault()}
                            className="w-10 text-center text-xs border border-gray-300 rounded py-0.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          <button aria-label={`Increase quantity for ${item.name}`} onClick={e => { e.preventDefault(); e.stopPropagation(); stepQty(item.id, +1); }} className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold leading-none">+</button>
                        </div>
                        <button
                          onClick={(e) => handleAddToCart(e, item.id)}
                          disabled={addingToCart[item.id]}
                          aria-label={addedToCart[item.id] ? `Added ${item.name} to cart` : `Add ${item.name} to cart`}
                          className={`w-full text-[11px] font-medium px-2 py-1 rounded transition-all ${
                            addedToCart[item.id]
                              ? "bg-green-50 text-green-700 border border-green-200"
                              : "bg-accent text-white hover:bg-accent/90"
                          }`}
                        >
                          {addingToCart[item.id] ? "..." : addedToCart[item.id] ? "Added!" : "+ Cart"}
                        </button>
                      </div>
                    )}
                    {!item.inStock && (
                      <div className="mt-auto pt-2">
                        <button
                          onClick={(e) => handleRequestSpecialOrder(e, item.id)}
                          disabled={requestingProduct[item.id] || requestedProducts.has(item.id)}
                          aria-label={requestedProducts.has(item.id) ? `${item.name} request submitted` : `Request 2-day shipping for ${item.name}`}
                          className={`w-full text-[11px] font-medium px-2 py-1 rounded transition-all ${
                            requestedProducts.has(item.id)
                              ? "bg-green-50 text-green-700 border border-green-200"
                              : "bg-orange-500 text-white hover:bg-orange-600"
                          }`}
                        >
                          {requestingProduct[item.id] ? "..." : requestedProducts.has(item.id) ? "Requested!" : "Request Item"}
                        </button>
                        <p className="text-[9px] text-gray-600 text-center mt-0.5">Availability may vary</p>
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {error && products.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <svg className="w-12 h-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
              <p className="text-gray-500 text-sm">Couldn't load the catalog right now.</p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Try again
              </button>
            </div>
          )}

          {loading && products.length === 0 ? (
            <CatalogSkeleton count={12} mode={viewMode} />
          ) : products.length === 0 && !error ? (
            <div className="text-center py-16">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
              <p className="text-gray-500 mb-2">No products found</p>
              {activeFilterCount > 0 && <button onClick={clearFilters} className="text-sm text-primary hover:underline">Clear all filters</button>}
            </div>
          ) : (
            <div className={`transition-opacity duration-200${isFetching ? " opacity-50 pointer-events-none" : ""}`}>
              {viewMode === "grid" ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {products.map((p: any) => (
                  <div key={p.id} className="flex flex-col">
                  <Link
                    to={`/products/${p.id}`}
                    className="bg-white border border-gray-200 rounded-lg p-3 hover:border-accent/40 hover:shadow-md transition-all group flex flex-col flex-1"
                  >
                    <div className="w-full aspect-square bg-gray-50 rounded-md flex items-center justify-center mb-3 overflow-hidden flex-shrink-0">
                      <CatalogGridImage
                        imageUrl={p.imageUrl}
                        productId={p.id}
                        dosageForm={p.dosageForm}
                        route={p.route}
                        alt={[p.name, p.strength, p.dosageForm].filter(Boolean).join(" ")}
                      />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <h3 className="text-xs font-semibold text-primary group-hover:text-accent line-clamp-2 leading-tight mb-1">{p.name}</h3>
                      {p.strength && <p className="text-[10px] text-gray-500 truncate">{p.strength}</p>}
                      <p className="text-[10px] text-gray-600 truncate mt-0.5">{(p.manufacturer && p.manufacturer !== "Unknown" ? p.manufacturer : null) || p.labelerName || "—"}</p>
                      {(p.dosageForm || p.route) && (
                        <p className="text-[10px] text-gray-600 truncate mt-0.5">
                          {p.dosageForm}{p.dosageForm && p.route ? " · " : ""}{p.route}
                        </p>
                      )}
                      {p.ndc && (
                        <p
                          className="text-[10px] text-gray-600 font-mono truncate mt-0.5"
                          title={`NDC: ${p.ndc}`}
                        >
                          NDC: {p.ndc}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${p.inStock ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-600"}`}>
                          <span className={`w-1 h-1 rounded-full ${p.inStock ? "bg-green-500" : "bg-blue-400"}`}></span>
                          {p.inStock ? "In Stock" : "Request Item"}
                        </span>
                        {(() => {
                          const custPrice = customerPriceMap[p.id];
                          if (custPrice !== undefined) {
                            return <span className="text-xs font-semibold text-green-600">${custPrice}</span>;
                          }
                          if (p.price && parseFloat(p.price) > 0) {
                            return <span className="text-xs font-semibold text-accent">${p.price}</span>;
                          }
                          return null;
                        })()}
                      </div>
                      {canAddToCart && (
                        <div className="mt-auto pt-2" onClick={e => e.preventDefault()}>
                          {p.inStock ? (
                            <>
                              <div className="flex items-center justify-center gap-1 mb-1">
                                <button aria-label={`Decrease quantity for ${p.name}`} onClick={e => { e.preventDefault(); e.stopPropagation(); stepQty(p.id, -1); }} className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold leading-none">−</button>
                                <input
                                  type="number"
                                  aria-label={`Quantity for ${p.name}`}
                                  min={1}
                                  value={getRaw(p.id)}
                                  onChange={e => { e.stopPropagation(); setRawInputs(prev => ({ ...prev, [p.id]: e.target.value })); }}
                                  onBlur={() => commitRaw(p.id)}
                                  onFocus={e => e.target.select()}
                                  onClick={e => e.preventDefault()}
                                  className="w-10 text-center text-xs border border-gray-300 rounded py-0.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                                <button aria-label={`Increase quantity for ${p.name}`} onClick={e => { e.preventDefault(); e.stopPropagation(); stepQty(p.id, +1); }} className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold leading-none">+</button>
                              </div>
                              <button
                                onClick={(e) => handleAddToCart(e, p.id)}
                                disabled={addingToCart[p.id]}
                                aria-label={addedToCart[p.id] ? `Added ${p.name} to cart` : `Add ${p.name} to cart`}
                                className={`w-full text-[11px] font-medium px-2 py-1 rounded transition-all ${
                                  addedToCart[p.id]
                                    ? "bg-green-50 text-green-700 border border-green-200"
                                    : "bg-accent text-white hover:bg-accent/90"
                                }`}
                              >
                                {addingToCart[p.id] ? "..." : addedToCart[p.id] ? "Added!" : "+ Cart"}
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={(e) => handleRequestSpecialOrder(e, p.id)}
                              disabled={requestingProduct[p.id] || requestedProducts.has(p.id)}
                              aria-label={requestedProducts.has(p.id) ? `${p.name} request submitted` : `Request 2-day shipping for ${p.name}`}
                              className={`w-full text-[11px] font-medium px-2 py-1 rounded transition-all ${
                                requestedProducts.has(p.id)
                                  ? "bg-green-50 text-green-700 border border-green-200"
                                  : "bg-orange-500 text-white hover:bg-orange-600"
                              }`}
                            >
                              {requestingProduct[p.id] ? "..." : requestedProducts.has(p.id) ? "Requested!" : "Request Item"}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </Link>
                  {p.genericName && (
                    <Link
                      to={`/drugs/${slugify(p.genericName)}`}
                      className="text-[10px] text-center text-gray-600 hover:text-accent truncate py-1 px-1"
                    >
                      {p.genericName.charAt(0).toUpperCase() + p.genericName.slice(1).toLowerCase()} drug info
                    </Link>
                  )}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <span className="text-sm text-gray-500">
                  Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                </span>
                <nav aria-label="Catalog pagination" className="flex items-center gap-1">
                  {page > 1 ? (
                    <Link to={buildPageHref(searchParams, 1)} className="px-2 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50">First</Link>
                  ) : (
                    <span aria-disabled="true" className="px-2 py-1.5 text-xs rounded border border-gray-300 opacity-40">First</span>
                  )}
                  {page > 1 ? (
                    <Link to={buildPageHref(searchParams, page - 1)} className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50">Prev</Link>
                  ) : (
                    <span aria-disabled="true" className="px-3 py-1.5 text-sm rounded border border-gray-300 opacity-40">Prev</span>
                  )}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (page <= 3) pageNum = i + 1;
                    else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = page - 2 + i;
                    return (
                      <Link key={pageNum} to={buildPageHref(searchParams, pageNum)} className={`w-8 h-8 text-sm rounded flex items-center justify-center ${page === pageNum ? "bg-primary text-white" : "border border-gray-300 hover:bg-gray-50"}`}>{pageNum}</Link>
                    );
                  })}
                  {page < totalPages ? (
                    <Link to={buildPageHref(searchParams, page + 1)} className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50">Next</Link>
                  ) : (
                    <span aria-disabled="true" className="px-3 py-1.5 text-sm rounded border border-gray-300 opacity-40">Next</span>
                  )}
                  {page < totalPages ? (
                    <Link to={buildPageHref(searchParams, totalPages)} className="px-2 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50">Last</Link>
                  ) : (
                    <span aria-disabled="true" className="px-2 py-1.5 text-xs rounded border border-gray-300 opacity-40">Last</span>
                  )}
                </nav>
              </div>
            </>
          ) : (
            <>
              <div className={`hidden md:grid gap-3 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 ${canAddToCart ? "grid-cols-[32px_48px_1fr_130px_120px_1fr_90px_100px_80px]" : user ? "grid-cols-[32px_48px_1fr_130px_120px_1fr_90px_100px]" : "grid-cols-[48px_1fr_130px_120px_1fr_90px_100px]"}`}>
                {user && <span></span>}
                <span></span>
                <span>Product</span>
                <span>NDC</span>
                <span>Strength</span>
                <span>Manufacturer</span>
                <span>Status</span>
                <span className="text-right">Price</span>
                {canAddToCart && <span></span>}
              </div>

              <div className="divide-y divide-gray-100">
                {favoritesInView > 0 && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-red-50/60 border-b border-red-100">
                    <svg className="w-4 h-4 text-red-400" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                    <span className="text-xs font-semibold text-red-500 uppercase tracking-wider">Your Favorites</span>
                  </div>
                )}
                {products.map((p: any, idx: number) => (
                  <React.Fragment key={p.id}>
                    {favoritesInView > 0 && idx === favoritesInView && (
                      <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200">
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">All Products</span>
                      </div>
                    )}
                  <ProductListCard
                    p={p}
                    isFavorite={favoriteIds.has(p.id)}
                    customerPrice={customerPriceMap[p.id]}
                    canAddToCart={!!canAddToCart}
                    hasUser={!!user}
                    userIsActive={user?.accountStatus === "active"}
                    rawInput={getRaw(p.id)}
                    addingToCart={!!addingToCart[p.id]}
                    addedToCart={!!addedToCart[p.id]}
                    requestingProduct={!!requestingProduct[p.id]}
                    productRequested={requestedProducts.has(p.id)}
                    onToggleFavorite={toggleFavorite}
                    onAddToCart={handleAddToCart}
                    onStepQty={stepQty}
                    onSetRaw={handleSetRaw}
                    onCommitRaw={commitRaw}
                    onRequestSpecialOrder={handleRequestSpecialOrder}
                    gridCols={canAddToCart ? "md:grid-cols-[32px_48px_1fr_130px_120px_1fr_90px_100px_80px]" : user ? "md:grid-cols-[32px_48px_1fr_130px_120px_1fr_90px_100px]" : "md:grid-cols-[48px_1fr_130px_120px_1fr_90px_100px]"}
                  />
                  </React.Fragment>
                ))}
              </div>

              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <span className="text-sm text-gray-500">
                  Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                </span>
                <nav aria-label="Catalog pagination" className="flex items-center gap-1">
                  {page > 1 ? (
                    <Link
                      to={buildPageHref(searchParams, 1)}
                      className="px-2 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50"
                    >
                      First
                    </Link>
                  ) : (
                    <span aria-disabled="true" className="px-2 py-1.5 text-xs rounded border border-gray-300 opacity-40">
                      First
                    </span>
                  )}
                  {page > 1 ? (
                    <Link
                      to={buildPageHref(searchParams, page - 1)}
                      className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
                    >
                      Prev
                    </Link>
                  ) : (
                    <span aria-disabled="true" className="px-3 py-1.5 text-sm rounded border border-gray-300 opacity-40">
                      Prev
                    </span>
                  )}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <Link
                        key={pageNum}
                        to={buildPageHref(searchParams, pageNum)}
                        className={`w-8 h-8 text-sm rounded flex items-center justify-center ${page === pageNum ? "bg-primary text-white" : "border border-gray-300 hover:bg-gray-50"}`}
                      >
                        {pageNum}
                      </Link>
                    );
                  })}
                  {page < totalPages ? (
                    <Link
                      to={buildPageHref(searchParams, page + 1)}
                      className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
                    >
                      Next
                    </Link>
                  ) : (
                    <span aria-disabled="true" className="px-3 py-1.5 text-sm rounded border border-gray-300 opacity-40">
                      Next
                    </span>
                  )}
                  {page < totalPages ? (
                    <Link
                      to={buildPageHref(searchParams, totalPages)}
                      className="px-2 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50"
                    >
                      Last
                    </Link>
                  ) : (
                    <span aria-disabled="true" className="px-2 py-1.5 text-xs rounded border border-gray-300 opacity-40">
                      Last
                    </span>
                  )}
                </nav>
              </div>
            </>
              )}
            </div>
          )}

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h2 className="text-sm font-semibold text-primary uppercase tracking-wider mb-3">Shop by Category</h2>
            <div className="flex flex-wrap gap-2 mb-6">
              {FEATURED_CATEGORY_LINKS.map((c) => (
                <Link
                  key={c.slug}
                  to={`/categories/${c.slug}`}
                  className="bg-white border border-gray-200 hover:border-accent/40 rounded-full px-3 py-1.5 text-sm text-primary hover:text-accent transition-colors"
                >
                  {c.name}
                </Link>
              ))}
            </div>
            {topManufacturers.length > 0 && (
              <>
                <h2 className="text-sm font-semibold text-primary uppercase tracking-wider mb-3">Top Manufacturers</h2>
                <div className="flex flex-wrap gap-2">
                  {topManufacturers.map((m) => (
                    <Link
                      key={m.slug}
                      to={`/manufacturers/${m.slug}`}
                      className="bg-white border border-gray-200 hover:border-accent/40 rounded-full px-3 py-1.5 text-sm text-primary hover:text-accent transition-colors"
                    >
                      {m.name}
                      <span className="text-gray-600 ml-1.5 text-xs">({m.productCount})</span>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {scannerOpen && (
        <Suspense fallback={null}>
          <BarcodeScanner
            onScan={handleScan}
            onClose={() => setScannerOpen(false)}
          />
        </Suspense>
      )}
    </>
  );
}

function FilterTag({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full">
      {label}
      <button onClick={(e) => { e.preventDefault(); onRemove(); }} aria-label={`Remove ${label} filter`} className="hover:text-primary/60">
        <svg aria-hidden="true" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
    </span>
  );
}
